import { WonKeyPanel } from './giveaway/WonKeyPanel'
import { Link } from 'react-router-dom'
import { useGiveaway, useGiveawayTypes } from '@/hooks/queries'
import { useGiveawayEntry } from '@/hooks/use-giveaway-entry'
import { useReferralLink } from '@/hooks/use-referral-link'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { giveawayPath, giveawayUrl } from '@/lib/slug'
import { entryOptionLabel, isFirstEntries, referralSummary, type Giveaway } from '@/models/giveaway'
import { useAuthStore } from '@/store/auth'
import { ReferralIcon } from './giveaway/ReferralIcon'
import { Avatar } from './ui/Avatar'
import { EntriesStrip } from './giveaway/EntriesStrip'
import { ValueBadge } from './giveaway/ValueBadge'
import { ShareButton } from './giveaway/ShareButton'
import { EntryHint } from './giveaway/EntryHint'
import { LinkedText } from './ui/LinkedText'
import { GameWebsiteLink } from './GameWebsiteLink'
import { MediaGallery } from './media/MediaGallery'
import { VideoEmbed } from './media/VideoEmbed'

interface GiveawayDetailsProps {
  /** Shown immediately while the full record is refetched. */
  giveaway: Giveaway
  onClose: () => void
}

/**
 * The quick look at a giveaway, opened from a feed card — a port of
 * components/GiveawayDetails.vue.
 *
 * It carries the same three affordances as the full page: open it properly,
 * copy its link, and see what entering actually involves.
 */
export function GiveawayDetails({ giveaway: initial, onClose }: GiveawayDetailsProps) {
  const { data } = useGiveaway(initial.id ?? undefined)
  const giveaway = data ?? initial

  const { requestEntry, enteringObjectiveId } = useGiveawayEntry(giveaway)
  const copyReferralLink = useReferralLink(giveaway)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { data: giveawayTypes } = useGiveawayTypes()
  const firstEntries = isFirstEntries(giveaway, giveawayTypes)

  const slug = giveaway.get_slug
  const owner = giveaway.owner

  return (
    <div className="overlay-wrapper">
      <div className="content">
        <div className="heading-row">
          <Avatar
            src={owner?.display_profile_picture.src}
            className="heading-avatar filled"
            label={`${owner?.display_name ?? 'Owner'}'s avatar`}
          />
          <div className="heading">
            <div className="title">
              <span>{giveaway.name}</span>
            </div>
            <div className="subtitle">
              event by
              {owner?.username ? (
                <Link className="username" to={`/u/${owner.username}`} onClick={onClose}>
                  &nbsp;{owner.display_name}
                </Link>
              ) : (
                <span className="username">&nbsp;{owner?.display_name}</span>
              )}
            </div>
          </div>
        </div>

        <div
          className="cover-media-row with-margin"
          style={backgroundImage(giveaway.display_cover.src)}
        />

        {giveaway.featured_video ? (
          <VideoEmbed
            url={giveaway.featured_video}
            title={`${giveaway.name ?? 'Giveaway'} video`}
            className="with-margin"
          />
        ) : null}

        {giveaway.game_info ? (
          <div className="with-margin" style={{ display: 'flex', justifyContent: 'center' }}>
            <ValueBadge gameInfo={giveaway.game_info} layout="inline" className="is-large" />
          </div>
        ) : null}

        <EntriesStrip giveaway={giveaway} className="with-margin" />

        {giveaway.description ? (
          <div className="content-row with-margin left small">
            <LinkedText text={giveaway.description} />
          </div>
        ) : null}

        <MediaGallery
          items={giveaway.display_gallery}
          title={giveaway.name ?? 'Giveaway'}
          className="with-margin"
        />

        <GameWebsiteLink game={giveaway.game_info} className="with-margin" />

        <div className="giveaway-actions with-margin" style={{ justifyContent: 'center' }}>
          <Link className="action-btn is-primary" to={giveawayPath(slug)} onClick={onClose}>
            Open giveaway page
          </Link>
          <ShareButton url={giveawayUrl(slug)} />
        </div>

        <WonKeyPanel giveaway={giveaway} className="with-margin" onNavigate={onClose} />

        <EntryHint giveaway={giveaway} firstEntries={firstEntries} className="with-margin" />

        {giveaway.giveaway_objective.map((objective) =>
          objective.is_referral ? (
            <div className="field-row with-margin" key={objective.key}>
              <div className="field">
                <button
                  type="button"
                  className="field-box button"
                  disabled={giveaway.has_ended}
                  onClick={() => void copyReferralLink(objective)}
                >
                  <div className="field-box-prepend">
                    <ReferralIcon />
                  </div>
                  <div className="field-box-content small">
                    <span>{objective.display_name}</span>
                    {!giveaway.has_ended ? (
                      <span className="field-box-hint">
                        {isAuthenticated
                          ? referralSummary(
                              objective,
                              firstEntries,
                              giveaway.require_all_objectives,
                            )
                          : 'Sign in to get your referral link'}
                      </span>
                    ) : null}
                  </div>
                  <div className="field-box-append">
                    {entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)}
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div className="field-row with-margin" key={objective.key}>
              <div className="field">
                <button
                  type="button"
                  className={cn('field-box button', objective.is_entered && 'objective-entered')}
                  disabled={objective.is_entered || giveaway.has_ended}
                  onClick={() => requestEntry(objective.id)}
                >
                  <div className="field-box-prepend">
                    <img src="/icons/discord.png" alt="" width={20} />
                  </div>
                  <div className="field-box-content small">{objective.display_name}</div>
                  <div className="field-box-append">
                    {objective.is_entered ? (
                      '✓'
                    ) : enteringObjectiveId === objective.id ? (
                      <span className="spinner-inline" aria-hidden="true" />
                    ) : (
                      entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)
                    )}
                  </div>
                </button>
              </div>
            </div>
          ),
        )}

        {giveaway.terms ? (
          <div className="content-row small with-margin">{giveaway.terms}</div>
        ) : null}

        <div className="footer">
          <div className="cta-row center">
            <button type="button" className="cta" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
